import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import InstagramWidget from "@/components/InstagramWidget";
import { MapPin, Plane, Mountain, Camera, Briefcase, User, Phone, Shield, Clock, Star, ChevronDown, Menu, X, ArrowRight, Instagram, Facebook, Mail, MessageCircle, Heart, Twitter, PhoneCall } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";

// XMLHttpRequest-based fetch to avoid Emergent script conflicts
const safeFetch = (url, options = {}) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(options.method || 'GET', url, true);
    const headers = options.headers || {};
    Object.entries(headers).forEach(([key, value]) => {
      if (value) xhr.setRequestHeader(key, value);
    });
    xhr.onload = function() {
      let data;
      try { data = xhr.responseText ? JSON.parse(xhr.responseText) : null; }
      catch { data = xhr.responseText; }
      resolve({
        ok: xhr.status >= 200 && xhr.status < 300,
        status: xhr.status,
        json: () => Promise.resolve(data),
        text: () => Promise.resolve(xhr.responseText)
      });
    };
    xhr.onerror = () => reject(new Error('Network error'));
    if (options.body) {
      xhr.send(typeof options.body === 'string' ? options.body : JSON.stringify(options.body));
    } else {
      xhr.send();
    }
  });
};

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cabvalley-ride/artifacts/2vw1iw9l_Untitled%20design-14.png";
const HERO_BG = "https://images.unsplash.com/photo-1666545385170-1dfae4d93605?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1NzZ8MHwxfHNlYXJjaHwzfHxrYXNobWlyJTIwbW91bnRhaW4lMjByb2FkJTIwc2NlbmljJTIwbGFuZHNjYXBlfGVufDB8fHx8MTc3MTI0MTM2MXww&ixlib=rb-4.1.0&q=85";
const KASHMIR_IMG = "https://images.unsplash.com/photo-1557116953-36724198de1c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxoYXBweSUyMGluZGlhbiUyMGZhbWlseSUyMHRvdXJpc3RzJTIwa2FzaG1pciUyMHNjZW5pY3xlbnwwfHx8fDE3NzEyNDEzODJ8MA&ixlib=rb-4.1.0&q=85";
const CAR_IMG = "https://images.unsplash.com/photo-1606200639236-00a51094e662?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxwcmVtaXVtJTIwbHV4dXJ5JTIwY2FyJTIwaW50ZXJpb3IlMjBsZWF0aGVyJTIwY2xlYW58ZW58MHx8fHwxNzcxMjQxMzYwfDA&ixlib=rb-4.1.0&q=85";

const fallbackServices = [
  { icon: MapPin, name: "Local Rides", desc: "City rides across the valley & nearby areas", tag: "Popular" },
  { icon: Plane, name: "Airport Transfers", desc: "Comfortable airport pickup & drop service", tag: null },
  { icon: Mountain, name: "Outstation Trips", desc: "Long-distance rides to any destination", tag: null },
  { icon: Camera, name: "Tourist Packages", desc: "Explore Kashmir with curated itineraries", tag: "Featured" },
  { icon: User, name: "Personal Booking", desc: "Dedicated cab for personal errands", tag: null },
  { icon: Briefcase, name: "Corporate Booking", desc: "Professional rides for businesses", tag: null },
];

const slugIconMap = {
  local_ride: MapPin, airport_transfer: Plane, outstation_trip: Mountain,
  tourist_package: Camera, personal_booking: User, corporate_booking: Briefcase,
};

const steps = [
  { num: "01", title: "Choose Your Service", desc: "Select from local rides, airport transfers, outstation trips and more." },
  { num: "02", title: "Admin Assigns Driver", desc: "Our team connects you with the best verified local driver instantly." },
  { num: "03", title: "Ride Safely", desc: "Enjoy a comfortable and secure journey across the beautiful valley." },
];

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.7, delay: i * 0.1, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const staggerContainer = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

export default function LandingPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [cms, setCms] = useState({});
  const [socialLinks, setSocialLinks] = useState({});
  const [platformSettings, setPlatformSettings] = useState({});
  const [menuItems, setMenuItems] = useState([]);
  const [featuredTrips, setFeaturedTrips] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  const [dynamicServices, setDynamicServices] = useState([]);
  const { scrollYProgress } = useScroll();
  const heroOpacity = useTransform(scrollYProgress, [0, 0.15], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.15], [1, 1.08]);

  useEffect(() => {
    if (user) {
      if (user.role === "super_admin") navigate("/super-admin");
      else if (user.role === "admin") navigate("/admin");
      else if (user.role === "driver") navigate("/driver");
      else navigate("/customer");
    }
  }, [user, navigate]);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    // Fetch CMS content
    safeFetch(`${API}/cms/public`).then(r => r.ok && r.json()).then(data => data && setCms(data)).catch(() => {});
    // Fetch social links
    safeFetch(`${API}/social-links/public`).then(r => r.ok && r.json()).then(data => data && setSocialLinks(data)).catch(() => {});
    // Fetch platform settings (support info)
    safeFetch(`${API}/platform-settings/public`).then(r => r.ok && r.json()).then(data => data && setPlatformSettings(data)).catch(() => {});
    // Fetch menu items
    safeFetch(`${API}/menu/public`).then(r => r.ok && r.json()).then(data => data && setMenuItems(data)).catch(() => {});
    // Fetch featured trips
    safeFetch(`${API}/trips/featured`).then(r => r.ok && r.json()).then(data => data && setFeaturedTrips(data)).catch(() => {});
    // Fetch gallery
    safeFetch(`${API}/gallery`).then(r => r.ok && r.json()).then(data => data && setGalleryItems(data)).catch(() => {});
    // Fetch dynamic services for homepage
    safeFetch(`${API}/service-types?context=homepage`).then(r => r.ok && r.json()).then(data => data && setDynamicServices(data)).catch(() => {});
  }, []);

  const hero = cms.hero || {};
  const brandStory = cms.brand_story || {};
  const footer = cms.footer || {};

  return (
    <div className="min-h-screen bg-white font-body overflow-x-hidden">
      {/* ===== NAVBAR ===== */}
      <nav
        data-testid="landing-navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? "glass-nav" : "glass-nav-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8 flex items-center justify-between h-[72px]">
          <a href="/" className="flex items-center gap-2.5">
            <img
              src={LOGO_URL}
              alt="Cab Valley"
              className={`h-12 w-auto object-contain transition-all duration-300 ${!scrolled ? "logo-light" : ""}`}
            />
          </a>
          <div className="hidden md:flex items-center gap-10">
            <a href="/" className={`nav-link-hover text-[13px] font-medium tracking-wide uppercase transition-colors ${scrolled ? "text-forest/70 hover:text-forest" : "text-white/80 hover:text-white"}`}>Home</a>
            <a href="#services" className={`nav-link-hover text-[13px] font-medium tracking-wide uppercase transition-colors ${scrolled ? "text-forest/70 hover:text-forest" : "text-white/80 hover:text-white"}`}>Services</a>
            <a href="#how-it-works" className={`nav-link-hover text-[13px] font-medium tracking-wide uppercase transition-colors ${scrolled ? "text-forest/70 hover:text-forest" : "text-white/80 hover:text-white"}`}>How It Works</a>
            <a href="#trips" className={`nav-link-hover text-[13px] font-medium tracking-wide uppercase transition-colors ${scrolled ? "text-forest/70 hover:text-forest" : "text-white/80 hover:text-white"}`}>Trips</a>
            <a href="#brand-story" className={`nav-link-hover text-[13px] font-medium tracking-wide uppercase transition-colors ${scrolled ? "text-forest/70 hover:text-forest" : "text-white/80 hover:text-white"}`}>Our Story</a>
            <a href="#explore" className={`nav-link-hover text-[13px] font-medium tracking-wide uppercase transition-colors ${scrolled ? "text-forest/70 hover:text-forest" : "text-white/80 hover:text-white"}`}>Explore</a>
            <LanguageSwitcher variant={scrolled ? "default" : "minimal"} className={scrolled ? "" : "text-white"} />
            <Button
              data-testid="nav-login-btn"
              onClick={() => navigate("/auth")}
              className="bg-amber hover:bg-amber-dark text-white rounded-full px-7 py-2.5 text-[13px] font-semibold tracking-wide shadow-lg shadow-amber/20 hover:shadow-xl hover:shadow-amber/30 transition-all duration-300 hover:-translate-y-0.5"
            >
              Sign In
            </Button>
          </div>
          <button
            data-testid="mobile-menu-toggle"
            className="md:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? (
              <X className={`w-6 h-6 ${scrolled ? "text-forest" : "text-white"}`} />
            ) : (
              <Menu className={`w-6 h-6 ${scrolled ? "text-forest" : "text-white"}`} />
            )}
          </button>
        </div>
        {/* Mobile Menu */}
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="md:hidden bg-white/95 backdrop-blur-xl border-t border-gray-100 px-5 py-6 space-y-4"
          >
            <a href="/" onClick={() => setMenuOpen(false)} className="block text-forest text-[15px] font-medium py-2">Home</a>
            <a href="#services" onClick={() => setMenuOpen(false)} className="block text-forest text-[15px] font-medium py-2">Services</a>
            <a href="#how-it-works" onClick={() => setMenuOpen(false)} className="block text-forest text-[15px] font-medium py-2">How It Works</a>
            <a href="#brand-story" onClick={() => setMenuOpen(false)} className="block text-forest text-[15px] font-medium py-2">Our Story</a>
            <a href="#explore" onClick={() => setMenuOpen(false)} className="block text-forest text-[15px] font-medium py-2">Explore</a>
            {platformSettings.support_phone && (
              <a href={`tel:${platformSettings.support_phone}`} onClick={() => setMenuOpen(false)} className="flex items-center gap-2 text-emerald-600 text-[15px] font-bold py-2">
                <PhoneCall className="w-5 h-5" /> Call to Book: {platformSettings.support_phone}
              </a>
            )}
            <div className="pt-2">
              <Button data-testid="mobile-login-btn" onClick={() => navigate("/auth")} className="w-full bg-amber hover:bg-amber-dark text-white rounded-full py-3 font-semibold text-[15px]">Sign In</Button>
            </div>
          </motion.div>
        )}
      </nav>

      {/* ===== CALL TO BOOK BANNER (Top sticky bar) ===== */}
      {platformSettings.support_phone && (
        <a
          href={`tel:${platformSettings.support_phone}`}
          data-testid="call-to-book-banner"
          className="fixed top-[72px] left-0 right-0 z-40 bg-gradient-to-r from-emerald-600 via-emerald-500 to-teal-500 text-white py-2.5 px-4 flex items-center justify-center gap-3 shadow-lg cursor-pointer hover:from-emerald-700 hover:via-emerald-600 hover:to-teal-600 transition-all duration-300"
        >
          <PhoneCall className="w-5 h-5 animate-pulse" />
          <span className="text-sm sm:text-base font-bold tracking-wide">
            Can't book online? Call us directly: {platformSettings.support_phone}
          </span>
          <span className="hidden sm:inline-flex items-center gap-1 bg-white/20 rounded-full px-3 py-1 text-xs font-semibold">
            Tap to Call
          </span>
        </a>
      )}

      {/* ===== FLOATING CALL BUTTON (Mobile) ===== */}
      {platformSettings.support_phone && (
        <a
          href={`tel:${platformSettings.support_phone}`}
          data-testid="floating-call-btn"
          className="fixed bottom-20 right-4 md:bottom-6 md:right-6 z-50 w-14 h-14 md:w-auto md:h-auto md:px-5 md:py-3 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full flex items-center justify-center gap-2 shadow-2xl shadow-emerald-500/40 hover:shadow-emerald-600/50 transition-all duration-300 hover:scale-105"
        >
          <PhoneCall className="w-6 h-6" />
          <span className="hidden md:inline font-bold text-sm">Call to Book</span>
        </a>
      )}

      {/* ===== HERO ===== */}
      <section data-testid="hero-section" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <motion.div
          style={{ scale: heroScale }}
          className="absolute inset-0 bg-cover bg-center"
        >
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" loading="lazy" />
        </motion.div>
        <div className="absolute inset-0 hero-gradient" />
        <div className="absolute inset-0 noise-overlay" />

        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 text-center px-5 max-w-5xl mx-auto"
        >
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-md border border-white/15 rounded-full px-5 py-2 mb-8"
          >
            <div className="w-2 h-2 bg-amber rounded-full animate-pulse" />
            <span className="text-white/80 text-xs sm:text-sm font-medium tracking-wide">
              {hero.badge_text || "Serving Jammu, Kashmir & Ladakh"}
            </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="font-heading text-5xl sm:text-6xl lg:text-8xl font-bold text-white leading-[1.05] tracking-tight"
          >
            {hero.title_line1 || "Your Valley."}
            <br />
            <span className="text-gradient-gold">{hero.title_line2 || "Your Ride."}</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35 }}
            className="mt-6 text-lg sm:text-xl text-white/60 max-w-2xl mx-auto font-light leading-relaxed"
          >
            {hero.subtitle || "Reliable mobility across Jammu, Kashmir & Ladakh. Now serving Poonch."}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4"
          >
            <Button
              data-testid="hero-book-btn"
              onClick={() => navigate("/auth")}
              className="btn-primary-glow bg-amber hover:bg-amber-dark text-white rounded-full px-10 py-6 text-[15px] font-bold shadow-2xl shadow-amber/25 tracking-wide"
              size="lg"
            >
              {hero.cta_primary || "Book a Ride"} <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            {platformSettings.support_phone && (
              <a
                href={`tel:${platformSettings.support_phone}`}
                data-testid="hero-call-btn"
                className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-4 text-[15px] font-bold shadow-2xl shadow-emerald-500/25 tracking-wide transition-all duration-300 hover:-translate-y-0.5"
              >
                <PhoneCall className="w-5 h-5" /> Call to Book
              </a>
            )}
            <Button
              data-testid="hero-driver-btn"
              variant="ghost"
              onClick={() => navigate("/auth")}
              className="text-white/70 hover:text-white hover:bg-white/10 rounded-full px-8 py-6 text-[15px] font-semibold transition-all duration-300"
              size="lg"
            >
              {hero.cta_secondary || "Drive with Us"}
            </Button>
          </motion.div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.8, duration: 1 }}
          className="absolute bottom-10 left-1/2 -translate-x-1/2 z-10"
        >
          <ChevronDown className="w-8 h-8 text-white/40 animate-bounce" />
        </motion.div>
      </section>

      {/* ===== TRUST BAR ===== */}
      <section className="bg-white py-8 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8 md:gap-16">
            {[
              { icon: Shield, label: "Verified Drivers" },
              { icon: Phone, label: "24/7 Support" },
              { icon: Clock, label: "On-Time Pickup" },
              { icon: Star, label: "4.9 Rating" },
            ].map((item, i) => (
              <motion.div
                key={`item-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-3"
              >
                <div className="w-10 h-10 rounded-xl bg-forest/5 flex items-center justify-center">
                  <item.icon className="w-5 h-5 text-forest" />
                </div>
                <span className="text-forest/70 text-sm font-medium">{item.label}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SERVICES ===== */}
      <section id="services" className="py-24 md:py-32 bg-[#FAFAF9]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="text-center mb-16 md:mb-20"
          >
            <span className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-4">Our Services</span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-forest tracking-tight">
              Travel Your Way
            </h2>
            <p className="mt-4 text-forest/50 max-w-xl mx-auto">
              From quick city rides to curated tourist packages, we've got your journey covered.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {(dynamicServices.length > 0 ? dynamicServices.map((svc, i) => ({
              icon: slugIconMap[svc.slug] || MapPin,
              name: svc.name,
              desc: svc.description || "",
              tag: i === 0 ? "Popular" : null,
              icon_url: svc.icon_url,
            })) : fallbackServices).map((service, i) => (
              <motion.div
                key={`item-${i}`}
                variants={fadeUp}
                custom={i}
                data-testid={`service-card-${i}`}
                className="group bg-white rounded-2xl p-7 shadow-sm hover:shadow-xl transition-all duration-500 border border-gray-100 hover:border-amber/20 cursor-pointer hover:-translate-y-1"
              >
                <div className="flex items-start justify-between mb-5">
                  <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-forest/5 to-forest/10 flex items-center justify-center group-hover:from-amber/10 group-hover:to-amber/20 transition-all duration-300 overflow-hidden">
                    {service.icon_url ? (
                      <img src={service.icon_url.startsWith("http") ? service.icon_url : `${API.replace('/api', '')}/${service.icon_url}`} alt="" className="w-8 h-8 object-contain" />
                    ) : (
                      <service.icon className="w-6 h-6 text-forest group-hover:text-amber transition-colors duration-300" />
                    )}
                  </div>
                  {service.tag && (
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-amber/10 text-amber px-3 py-1.5 rounded-full">
                      {service.tag}
                    </span>
                  )}
                </div>
                <h3 className="font-heading font-bold text-lg text-forest mb-2 group-hover:text-amber transition-colors duration-300">
                  {service.name}
                </h3>
                <p className="text-forest/50 text-sm leading-relaxed">{service.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== BRAND STORY ===== */}
      <section id="brand-story" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
            >
              <span className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-4">Our Story</span>
              <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-forest tracking-tight leading-tight">
                {brandStory.title || "Built for the Valley"}
              </h2>
              <div className="mt-6 space-y-4 text-forest/60 leading-relaxed">
                <p>
                  {brandStory.text || "Cab Valley was born from the need for reliable, safe transportation in the beautiful valleys of Jammu, Kashmir & Ladakh. We connect travelers with trusted local drivers who know these roads like the back of their hands."}
                </p>
                <p>
                  Our mission is simple: make travel across the region safe, affordable, and memorable. Whether you're a tourist exploring the majestic landscapes or a local commuting daily, Cab Valley is your trusted mobility partner.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-6">
                {[
                  { num: "500+", label: "Rides Completed" },
                  { num: "50+", label: "Verified Drivers" },
                  { num: "4.9", label: "Average Rating" },
                ].map((stat, i) => (
                  <div key={`item-${i}`} className="text-center">
                    <p className="font-heading font-bold text-3xl text-forest">{stat.num}</p>
                    <p className="text-xs text-forest/50 uppercase tracking-wider mt-1">{stat.label}</p>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
              className="relative"
            >
              <div className="aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl">
                <img src={KASHMIR_IMG} alt="Kashmir Valley" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl p-5 shadow-xl">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl bg-amber/10 flex items-center justify-center">
                    <Heart className="w-6 h-6 text-amber" />
                  </div>
                  <div>
                    <p className="font-heading font-bold text-forest">Made with Love</p>
                    <p className="text-xs text-forest/50">For the Valley</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== EXPLORE KASHMIR ===== */}
      <section id="explore" className="relative py-24 md:py-32 overflow-hidden">
        <div className="absolute inset-0">
          <img src={KASHMIR_IMG} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-forest/95 via-forest/85 to-forest/70" />
        <div className="absolute inset-0 noise-overlay" />

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <motion.span
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-5"
            >
              Explore With Us
            </motion.span>
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight"
            >
              Discover the Magic of{" "}
              <span className="text-gradient-gold">Kashmir</span>
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
              className="mt-5 text-lg text-white/60 max-w-lg leading-relaxed"
            >
              From the serene Dal Lake to the snow-capped peaks of Gulmarg, explore paradise with our curated packages.
            </motion.p>
          </div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            variants={staggerContainer}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-4"
          >
            {[
              { icon: Mountain, label: "Gulmarg Trips" },
              { icon: MapPin, label: "Dal Lake Tours" },
              { icon: Camera, label: "Photo Stops" },
              { icon: Star, label: "Family Tours" },
            ].map((item, i) => (
              <motion.div
                key={`item-${i}`}
                variants={fadeUp}
                custom={i}
                className="glass-card rounded-2xl p-5 flex items-center gap-4"
              >
                <div className="w-10 h-10 rounded-xl bg-amber/20 flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-amber" />
                </div>
                <span className="text-white/90 text-sm font-semibold">{item.label}</span>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={4}
          >
            <Button
              data-testid="explore-cta-btn"
              onClick={() => navigate("/auth")}
              className="mt-10 btn-primary-glow bg-amber hover:bg-amber-dark text-white rounded-full px-10 py-6 text-[15px] font-bold shadow-2xl shadow-amber/25 tracking-wide"
              size="lg"
            >
              Explore Packages <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-24 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-80px" }}
            variants={fadeUp}
            className="text-center mb-16 md:mb-20"
          >
            <span className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-4">How It Works</span>
            <h2 className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-forest tracking-tight">
              Book in 3 Simple Steps
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={staggerContainer}
            className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 relative"
          >
            {/* Connecting line (desktop) */}
            <div className="hidden md:block absolute top-[60px] left-[20%] right-[20%] h-[2px] bg-gradient-to-r from-amber/20 via-amber/40 to-amber/20" />

            {steps.map((step, i) => (
              <motion.div
                key={`item-${i}`}
                variants={fadeUp}
                custom={i}
                data-testid={`step-card-${i}`}
                className="relative text-center"
              >
                <div className="relative inline-flex items-center justify-center w-[72px] h-[72px] bg-gradient-to-br from-amber to-amber-dark rounded-2xl mb-7 shadow-lg shadow-amber/20">
                  <span className="text-white font-heading font-bold text-2xl">{step.num}</span>
                </div>
                <h3 className="font-heading font-bold text-xl text-forest mb-3">{step.title}</h3>
                <p className="text-forest/50 leading-relaxed max-w-xs mx-auto">{step.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ===== POPULAR TRIPS ===== */}
      {featuredTrips.length > 0 && (
        <section id="trips" className="py-24 md:py-32 bg-[#FAFAF9]">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={staggerContainer} className="text-center mb-16">
              <span className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-4">Popular Routes</span>
              <h2 className="font-heading font-black text-4xl md:text-5xl text-forest tracking-tight">Featured Trips</h2>
              <p className="text-forest/50 mt-4 max-w-xl mx-auto">Explore the most popular routes across Jammu, Kashmir & Ladakh</p>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredTrips.map((trip, i) => (
                <motion.div key={trip.trip_id || i} variants={fadeUp}>
                  <div className="bg-white rounded-2xl shadow-sm hover:shadow-lg transition-shadow overflow-hidden group cursor-pointer" onClick={() => navigate("/auth")}>
                    <div className="h-40 bg-gradient-to-br from-forest/20 to-forest/5 flex items-center justify-center">
                      <div className="text-center">
                        <p className="font-heading font-bold text-2xl text-forest">{trip.from_location}</p>
                        <span className="text-forest/40 text-sm">to</span>
                        <p className="font-heading font-bold text-2xl text-forest">{trip.to_location}</p>
                      </div>
                    </div>
                    <div className="p-5">
                      <h3 className="font-heading font-bold text-lg text-slate-900">{trip.title}</h3>
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{trip.description}</p>
                      <div className="flex items-center justify-between mt-4 pt-3 border-t">
                        <span className="text-sm text-slate-400">{trip.estimated_duration}</span>
                        <span className="font-heading font-bold text-xl text-forest">{"\u20B9"}{trip.estimated_fare}</span>
                      </div>
                    </div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ===== GALLERY ===== */}
      {galleryItems.length > 0 && (
        <section id="gallery" className="py-24 md:py-32 bg-white">
          <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.3 }} variants={staggerContainer} className="text-center mb-16">
              <span className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-4">Gallery</span>
              <h2 className="font-heading font-black text-4xl md:text-5xl text-forest tracking-tight">Moments & Destinations</h2>
            </motion.div>
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={staggerContainer} className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {galleryItems.map((item, i) => (
                <motion.div key={item.item_id || i} variants={fadeUp} className="rounded-2xl overflow-hidden group relative aspect-square">
                  <img src={item.image_url} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-4">
                    <div><p className="text-white font-medium text-sm">{item.title}</p>{item.caption && <p className="text-white/70 text-xs">{item.caption}</p>}</div>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>
      )}

      {/* ===== DRIVER CTA ===== */}
      <section className="relative py-24 md:py-28 bg-forest overflow-hidden">
        <div className="absolute inset-0 noise-overlay" />
        <div className="absolute top-0 right-0 w-1/2 h-full opacity-10">
          <img src={CAR_IMG} alt="" className="w-full h-full object-cover" loading="lazy" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <motion.span
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              className="inline-block text-amber font-semibold text-xs uppercase tracking-[0.2em] mb-5"
            >
              Join Our Fleet
            </motion.span>
            <motion.h2
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={1}
              className="font-heading text-3xl sm:text-4xl md:text-5xl font-bold text-white tracking-tight leading-tight"
            >
              Earn with{" "}
              <span className="text-gradient-gold">Cab Valley</span>
            </motion.h2>
            <motion.p
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={2}
              className="mt-5 text-lg text-white/60 max-w-lg leading-relaxed"
            >
              Join a growing network of trusted drivers across Jammu, Kashmir & Ladakh. Flexible hours, fair earnings, and high demand.
            </motion.p>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
              className="mt-8 flex flex-wrap gap-4"
            >
              {["Flexible Hours", "Weekly Payouts", "High Demand", "Support Team"].map((benefit, i) => (
                <motion.div key={`item-${i}`} variants={fadeUp} className="flex items-center gap-2 bg-white/8 backdrop-blur-sm rounded-full px-5 py-2.5 border border-white/10">
                  <div className="w-1.5 h-1.5 bg-amber rounded-full" />
                  <span className="text-white/80 text-sm font-medium">{benefit}</span>
                </motion.div>
              ))}
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUp}
              custom={4}
            >
              <Button
                data-testid="become-driver-btn"
                onClick={() => navigate("/auth")}
                className="mt-10 btn-primary-glow bg-amber hover:bg-amber-dark text-white rounded-full px-10 py-6 text-[15px] font-bold shadow-2xl shadow-amber/25 tracking-wide"
                size="lg"
              >
                Become a Driver <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ===== INSTAGRAM SECTION ===== */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            className="text-center mb-10"
          >
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-forest">
              Follow Our Journey
            </h2>
            <p className="text-slate-600 mt-3 max-w-2xl mx-auto">
              See the beautiful destinations and happy travelers on our Instagram
            </p>
          </motion.div>
          
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            custom={1}
            className="max-w-md mx-auto"
          >
            <InstagramWidget username={socialLinks.instagram?.split('/').pop() || "cabvalley"} limit={6} />
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#0A1F15] pt-16 pb-8">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-10 md:gap-8 pb-12 border-b border-white/8">
            {/* Brand */}
            <div className="md:col-span-2">
              <img src={LOGO_URL} alt="Cab Valley" className="h-14 w-auto logo-light mb-5" />
              <p className="text-white/40 text-sm leading-relaxed max-w-sm">
                {footer.description || "Serving Jammu, Kashmir & Ladakh. Currently operating in Poonch. Expanding to more cities soon."}
              </p>
              {/* Social Links */}
              <div className="mt-6 flex gap-4">
                {socialLinks.instagram && (
                  <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber/20 flex items-center justify-center transition-colors">
                    <Instagram className="w-5 h-5 text-white/60 hover:text-amber" />
                  </a>
                )}
                {socialLinks.facebook && (
                  <a href={socialLinks.facebook} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber/20 flex items-center justify-center transition-colors">
                    <Facebook className="w-5 h-5 text-white/60 hover:text-amber" />
                  </a>
                )}
                {socialLinks.twitter && (
                  <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber/20 flex items-center justify-center transition-colors">
                    <Twitter className="w-5 h-5 text-white/60 hover:text-amber" />
                  </a>
                )}
                {socialLinks.whatsapp && (
                  <a href={`https://wa.me/${socialLinks.whatsapp.replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber/20 flex items-center justify-center transition-colors">
                    <MessageCircle className="w-5 h-5 text-white/60 hover:text-amber" />
                  </a>
                )}
                {platformSettings.support_email && (
                  <a href={`mailto:${platformSettings.support_email}`} className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber/20 flex items-center justify-center transition-colors">
                    <Mail className="w-5 h-5 text-white/60 hover:text-amber" />
                  </a>
                )}
                {platformSettings.support_phone && (
                  <a href={`tel:${platformSettings.support_phone}`} className="w-10 h-10 rounded-full bg-white/5 hover:bg-amber/20 flex items-center justify-center transition-colors">
                    <Phone className="w-5 h-5 text-white/60 hover:text-amber" />
                  </a>
                )}
              </div>
            </div>

            {/* Links */}
            <div>
              <h4 className="text-white/70 font-semibold text-sm uppercase tracking-wider mb-5">Quick Links</h4>
              <ul className="space-y-3">
                <li><a href="/" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Home</a></li>
                <li><a href="#services" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Services</a></li>
                <li><a href="#brand-story" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Our Story</a></li>
                <li><a href="#how-it-works" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">How It Works</a></li>
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-white/70 font-semibold text-sm uppercase tracking-wider mb-5">Legal</h4>
              <ul className="space-y-3">
                <li><a href="/policies/privacy_policy" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Privacy Policy</a></li>
                <li><a href="/policies/terms_of_service" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Terms of Service</a></li>
                <li><a href="/policies/refund_policy" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Refund Policy</a></li>
                <li><a href="/policies/cancellation_policy" className="text-white/40 hover:text-amber text-sm transition-colors duration-200">Cancellation Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
            <p className="text-white/25 text-xs tracking-wide">
              &copy; {new Date().getFullYear()} Cab Valley. {footer.tagline || "Built for the Valley. Driven by Trust."}
            </p>
            <p className="text-white/20 text-xs">Made with ❤️ in Kashmir</p>
          </div>
        </div>
      </footer>

      {/* ===== MOBILE STICKY CTA ===== */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-white via-white to-transparent md:hidden z-40">
        <Button
          data-testid="mobile-sticky-cta"
          onClick={() => navigate("/auth")}
          className="w-full bg-forest hover:bg-forest-dark text-white rounded-full py-4 font-semibold shadow-xl"
        >
          Book a Ride <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </div>
    </div>
  );
}
